import { useTranslation } from '@jq4race/i18n';
import type { PhotoAngle } from '@jq4race/shared';
import { Badge, Button } from '@jq4race/ui';
import { Camera, CheckCircle2, RefreshCw, RotateCcw } from 'lucide-react';

export type SlotStatus = 'empty' | 'capturing' | 'uploading' | 'done' | 'error';

export interface PhotoCaptureSlotProps {
  angle: PhotoAngle;
  label: string;
  status: SlotStatus;
  thumbnailUrl?: string;
  errorMessage?: string;
  onCapture: () => void;
  onRetake: () => void;
}

const ANGLE_LABEL_KEYS: Record<PhotoAngle, string> = {
  front: 'photoAngles.front',
  back: 'photoAngles.back',
  driver: 'photoAngles.driver',
  passenger: 'photoAngles.passenger',
};

/**
 * Single angle slot in the photo capture grid.
 * State machine: empty | capturing | uploading | done | error.
 * Touch-target minimum: 48px (h-12) per design system requirements.
 */
export function PhotoCaptureSlot({
  angle,
  label,
  status,
  thumbnailUrl,
  errorMessage,
  onCapture,
  onRetake,
}: PhotoCaptureSlotProps) {
  const { t } = useTranslation('checkin');
  const displayLabel = label || t(ANGLE_LABEL_KEYS[angle]);

  // ── Empty ────────────────────────────────────────────────────────────────

  if (status === 'empty') {
    return (
      <div className="flex flex-col items-center gap-2">
        <span className="jq-caption text-muted-foreground">{displayLabel}</span>
        <button
          type="button"
          className="flex h-[72px] w-full flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-border bg-secondary text-muted-foreground transition-colors hover:border-ring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 min-h-[48px]"
          onClick={onCapture}
          aria-label={t('photoSlot.captureAria', { label: displayLabel })}
        >
          <Camera className="size-5" strokeWidth={1.5} />
          <span className="text-[12px]">{t('photoSlot.tapToCapture')}</span>
        </button>
      </div>
    );
  }

  // ── Capturing ────────────────────────────────────────────────────────────

  if (status === 'capturing') {
    return (
      <div className="flex flex-col items-center gap-2">
        <span className="jq-caption text-muted-foreground">{displayLabel}</span>
        <div
          role="status"
          className="flex h-[72px] w-full items-center justify-center rounded-sm border border-border bg-secondary"
        >
          <span className="jq-caption text-muted-foreground">{t('photoSlot.capturing')}</span>
        </div>
      </div>
    );
  }

  // ── Uploading ────────────────────────────────────────────────────────────

  if (status === 'uploading') {
    return (
      <div className="flex flex-col items-center gap-2">
        <span className="jq-caption text-muted-foreground">{displayLabel}</span>
        <div className="relative h-[72px] w-full overflow-hidden rounded-sm border border-border">
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={t('photoSlot.previewAlt', { label: displayLabel })}
              className="h-full w-full object-cover"
            />
          )}
          <div
            role="status"
            className="absolute inset-0 flex items-center justify-center bg-black/50"
          >
            <span className="jq-caption text-white">{t('photoSlot.uploading')}</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Done ─────────────────────────────────────────────────────────────────

  if (status === 'done') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex w-full items-center justify-between">
          <span className="jq-caption text-muted-foreground">{displayLabel}</span>
          <Badge
            variant="outline"
            className="border-accent/40 bg-accent/10 text-jq-gold-bright h-5 px-1.5"
          >
            <CheckCircle2 className="size-3 mr-0.5" strokeWidth={2} />
            {t('photoSlot.done')}
          </Badge>
        </div>
        <div className="relative h-[72px] w-full overflow-hidden rounded-sm border border-border">
          <img
            src={thumbnailUrl}
            alt={t('photoSlot.capturedAlt', { label: displayLabel })}
            className="h-full w-full object-cover"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-10 w-full text-[13px]"
          onClick={onRetake}
          aria-label={t('photoSlot.retakeAria', { label: displayLabel })}
        >
          <RotateCcw className="size-3.5" />
          {t('photoSlot.retake')}
        </Button>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="jq-caption text-muted-foreground">{displayLabel}</span>
      <div className="flex h-[72px] w-full flex-col items-center justify-center gap-1 rounded-sm border border-destructive/60 bg-destructive/10 px-2">
        <span className="text-[12px] text-center text-destructive">
          {errorMessage ?? t('photoSlot.uploadFailed')}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        className="h-10 w-full text-[13px] text-destructive"
        onClick={onCapture}
        aria-label={t('photoSlot.retryAria', { label: displayLabel })}
      >
        <RefreshCw className="size-3.5" />
        {t('photoSlot.retry')}
      </Button>
    </div>
  );
}
